import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const HomeVisitList = ({ data = [], ytdTotal = 0, filters = {} }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter((item) => {
    // 1. Global Search
    const searchStr = `${item.client_name || ""} ${item.outcome || ""}`.toLowerCase();
    const matchesSearch = searchStr.includes(filterText.toLowerCase());

    // 2. Specific Filters
    const matchesClient = !filters.clientName || item.client_name.toLowerCase().includes(filters.clientName.toLowerCase());
    const matchesOutcome = !filters.outcome || item.outcome === filters.outcome;
    
    // 3. Date Range
    let matchesDate = true;
    if (filters.startDate && item.scheduled_date < filters.startDate) matchesDate = false;
    if (filters.endDate && item.scheduled_date > filters.endDate) matchesDate = false;

    return matchesSearch && matchesClient && matchesOutcome && matchesDate;
  });

  const totalRows = filteredData.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      name: "Client Name",
      selector: (row) => row.client_name || "—",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.client_name || "—"}</span>
    },
    {
      name: "Request Details",
      selector: (row) => row.request_details || "—",
      sortable: true,
      cell: (row) => <span className="text-capitalize">{row.request_details || "—"}</span>
    },
    {
      name: "Scheduled Date",
      selector: (row) => row.scheduled_date || "—",
      sortable: true,
    },
    {
      name: "Date Updates",
      selector: (row) => (
        <span className="badge bg-label-info">{row.date_updates || 0} Changes</span>
      ),
      sortable: true,
    },
    {
      name: "Visits for Client",
      selector: (row) => row.total_visits || 0,
      sortable: true,
    },
    {
      name: "Outcome",
      selector: (row) => (
        <span className={`badge ${row.outcome === 'Residential' ? 'bg-label-primary' : row.outcome === 'DRP' ? 'bg-label-success' : 'bg-label-warning'} text-capitalize`}>
          {row.outcome || "Pending"}
        </span>
      ),
      sortable: true,
    },
  ];

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text("Home Visit Reports", 14, 15);
    
    // Summary data
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Total Visits (YTD): ${ytdTotal}`, 14, 25);
    
    // Table reset
    doc.setTextColor(0);
    const tableColumn = ["Client Name", "Details", "Scheduled Date", "Visits", "Outcome"];
    const tableRows = filteredData.map(d => [
      d.client_name || "—",
      d.request_details || "—",
      d.scheduled_date || "—",
      d.total_visits || 0,
      d.outcome || "Pending"
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
    });
    doc.save("home_visit_report.pdf");
  };

  const getCSVData = () => {
    const headers = ["Client Name", "Request Details", "Scheduled Date", "Visits for Client", "Outcome"];
    const rows = filteredData.map(d => [
      d.client_name || "—",
      d.request_details || "—",
      d.scheduled_date || "—",
      d.total_visits || 0,
      d.outcome || "Pending"
    ]);

    return [
      ["Home Visit Report Summary"],
      ["Total Visits (YTD)", ytdTotal],
      [], // blank line
      headers,
      ...rows
    ];
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20, 25].map((option) => (<option key={option} value={option}>{option}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><CSVLink data={getCSVData()} filename="home_visit_report.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
            </ul>
          </div>
          <input
            type="text"
            placeholder="Search visits..."
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">Showing {start}-{end} of {totalRows} records</h6>
        <div className="text-muted small">
          Total Visits (YTD): <span className="fw-bold">{ytdTotal}</span>
        </div>
      </div>
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No home visit data found</div>}
        />
      </div>
    </div>
  );
};

export default HomeVisitList;
